/**
 * AutoLinkText — scans a block of plain prose and turns any mention of a
 * known Knowledge Graph entity (person, place, organization, event,
 * disease) into a clickable link to its canonical KG page
 * (/person/:slug, /place/:slug, …).
 *
 * How it works:
 *   1. On mount, fetches `/api/kg/link-index` once (cached in memory for
 *      the current SPA session) — returns up to 600 top entities.
 *   2. Builds a single case-insensitive regex from all names + aliases,
 *      sorted longest-first so "United States of America" wins over "United".
 *   3. Renders text as a mixed array of strings and <Link> elements. Each
 *      unique entity is auto-linked at most `perEntityMax` times per body
 *      (default 1) to avoid cluttering the article.
 *
 * Usage:
 *   <AutoLinkText text={article.body} />
 *   <AutoLinkText text={paragraph} perEntityMax={2} />
 *
 * Falls back to plain text if the index hasn't loaded yet, or if the
 * text is empty. Never throws — degrades silently.
 */
import { useEffect, useState, useMemo, Fragment } from "react";
import { Link } from "react-router-dom";
import { http } from "@/lib/api";

// Module-level cache so multiple <AutoLinkText> instances share it.
let _cache = { promise: null, data: null, expires_at: 0 };
const CACHE_TTL_MS = 5 * 60 * 1000;   // 5 min

// Slug-prefix per entity_type.
const TYPE_PREFIX = {
  person:       "/person",
  place:        "/place",
  organization: "/organization",
  event:        "/event",
  disease:      "/disease",
};

// Only auto-link words with letters. Skip pure numbers, single chars.
function _isLinkable(name) {
  if (!name || name.length < 3) return false;
  if (!/[a-zA-Z]/.test(name)) return false;
  return true;
}

async function _loadIndex() {
  const now = Date.now();
  if (_cache.data && _cache.expires_at > now) return _cache.data;
  if (_cache.promise) return _cache.promise;
  _cache.promise = (async () => {
    try {
      const { data } = await http.get("/kg/link-index?limit=600");
      _cache.data = data;
      _cache.expires_at = Date.now() + CACHE_TTL_MS;
      return data;
    } catch {
      // Silent fail — article still renders as plain text.
      _cache.data = { items: [] };
      _cache.expires_at = Date.now() + 30_000;   // brief cache on failure
      return _cache.data;
    } finally {
      _cache.promise = null;
    }
  })();
  return _cache.promise;
}

// Build a combined regex + label→(slug, type) map.
function _buildMatcher(items) {
  const labelMap = new Map();     // normalized lowercase label → { slug, entity_type, display }
  for (const it of items) {
    const candidates = [it.name, ...(it.aliases || [])];
    for (const label of candidates) {
      if (!_isLinkable(label)) continue;
      const key = label.toLowerCase();
      if (!labelMap.has(key)) {
        labelMap.set(key, { slug: it.slug, entity_type: it.entity_type, display: label });
      }
    }
  }
  const labels = Array.from(labelMap.keys()).sort((a, b) => b.length - a.length);
  if (labels.length === 0) return { regex: null, labelMap };
  const pattern = labels
    .map((l) => l.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"))
    .join("|");
  // \b word boundaries + case-insensitive. Global for iteration.
  const regex = new RegExp(`\\b(${pattern})\\b`, "gi");
  return { regex, labelMap };
}

export default function AutoLinkText({ text, perEntityMax = 1, className, testid, renderPlain }) {
  const [items, setItems] = useState(_cache.data?.items || null);

  useEffect(() => {
    if (items) return;
    let alive = true;
    _loadIndex().then((data) => { if (alive) setItems(data.items || []); });
    return () => { alive = false; };
  }, [items]);

  const nodes = useMemo(() => {
    if (!text) return null;
    const wrapPlain = typeof renderPlain === "function" ? renderPlain : (s) => s;
    if (!items || items.length === 0) return [wrapPlain(text)];
    const { regex, labelMap } = _buildMatcher(items);
    if (!regex) return [wrapPlain(text)];

    const out = [];
    const linkedCount = new Map();      // slug → count
    let lastIdx = 0;
    let m;
    regex.lastIndex = 0;
    while ((m = regex.exec(text)) !== null) {
      const matched = m[0];
      const key = matched.toLowerCase();
      const info = labelMap.get(key);
      if (!info) continue;
      const already = linkedCount.get(info.slug) || 0;
      if (already >= perEntityMax) continue;

      // Emit the preceding plain segment.
      if (m.index > lastIdx) out.push(wrapPlain(text.slice(lastIdx, m.index)));
      // Emit the link. Preserve the original casing of the matched text.
      const prefix = TYPE_PREFIX[info.entity_type] || "/entity";
      out.push(
        <Link
          key={`${info.slug}-${m.index}`}
          to={`${prefix}/${info.slug}`}
          className="underline decoration-dotted underline-offset-2 hover:decoration-solid"
          style={{ color: "var(--wofy-primary, #b25d38)" }}
          data-testid={`kg-autolink-${info.slug}`}
          title={`Knowledge Graph · ${info.entity_type}`}
        >
          {matched}
        </Link>,
      );
      linkedCount.set(info.slug, already + 1);
      lastIdx = m.index + matched.length;
    }
    if (lastIdx < text.length) out.push(wrapPlain(text.slice(lastIdx)));
    return out;
  }, [text, items, perEntityMax, renderPlain]);

  if (!text) return null;
  return (
    <span className={className} data-testid={testid || "auto-link-text"}>
      {(nodes || [text]).map((n, i) => (
        <Fragment key={i}>{n}</Fragment>
      ))}
    </span>
  );
}
